import { clear, del, get, entries as idbEntries, keys as idbKeys, set } from 'idb-keyval';

const INDEXED_DB_CONNECTION_LOST_PATTERNS = [
  'Connection to Indexed Database server lost',
  'A mutation operation was attempted on a database that did not succeed',
];

export function isIndexedDBConnectionError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';
  const code = typeof DOMException !== 'undefined' && error instanceof DOMException ? error.code : undefined;
  const messageLower = message.toLowerCase();
  return (
    INDEXED_DB_CONNECTION_LOST_PATTERNS.some((pattern) => message.includes(pattern)) ||
    (name === 'UnknownError' && messageLower.includes('indexed')) ||
    // iOS/WebKit closes IndexedDB connections on backgrounding; opening a transaction on the
    // stale handle then throws InvalidStateError (DOMException code 11), e.g.
    // "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing."
    messageLower.includes('database connection is closing') ||
    ((name === 'InvalidStateError' || code === 11) && messageLower.includes('closing'))
  );
}

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const getValue = (key: string) =>
  get(key)
    .then((entry) => {
      if (!entry || typeof entry !== 'object' || !('ts' in entry)) return entry;
      if (Date.now() - entry.ts > (entry.ttl ?? DEFAULT_TTL_MS)) {
        del(key).catch(() => {});
        return undefined;
      }
      return entry.value;
    })
    .catch(() => undefined);

export const setValue = (key: string, value: any, ttl: number = DEFAULT_TTL_MS) =>
  set(key, { value, ts: Date.now(), ttl: ttl }).catch(() => {});
export const clearStore = () => clear().catch(() => {});
export const removeValue = (key: string) => del(key).catch(() => {});
export const entries = () => idbEntries().catch(() => [] as [IDBValidKey, unknown][]);
export const keys = () => idbKeys().catch(() => [] as IDBValidKey[]);
